import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, doc, addDoc, deleteDoc, onSnapshot, orderBy, query, where, serverTimestamp,
} from 'firebase/firestore';
import QRCode from 'qrcode';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Admin Assets page (CAL-V2 follow-up, 2026-05-18).
//
// Top-level page (was embedded inside Settings). Three jobs:
//   1. List + create + delete assets (existing behaviour)
//   2. Bulk-import assets from a CSV file (NEW — Task #96)
//   3. View per-asset ticket history (NEW — Task #98)
//
// An "asset" is a piece of equipment a customer can scan a QR sticker on to instantly
// raise a service ticket pre-filled with the asset's identity. Each asset belongs to a
// vendor, has a human-readable name + site, and optionally a default skill category to
// pre-select on the ticket form.

export default function Assets() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [assets, setAssets] = useState(null);
  const [skills, setSkills] = useState([]);
  const [name, setName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [defaultSkillCategoryId, setDefaultSkillCategoryId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [qrFor, setQrFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (!vendorId) return;
    const unsubA = onSnapshot(
      collection(db, 'vendors', vendorId, 'assets'),
      (snap) => setAssets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    const unsubS = onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
    return () => { unsubA(); unsubS(); };
  }, [vendorId]);

  async function createAsset(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      await addDoc(collection(db, 'vendors', vendorId, 'assets'), {
        name: name.trim(),
        siteName: siteName.trim() || null,
        defaultSkillCategoryId: defaultSkillCategoryId || null,
        notes: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setName('');
      setSiteName('');
      setDefaultSkillCategoryId('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function removeAsset(assetId) {
    if (!confirm('Delete this asset? Any existing QR stickers will stop working.')) return;
    try { await deleteDoc(doc(db, 'vendors', vendorId, 'assets', assetId)); }
    catch (err) { setError(err.message); }
  }

  // Filtered + sorted assets for the list — by name match (case-insensitive) on name
  // or siteName. Empty search = show all.
  const filtered = useMemo(() => {
    if (!assets) return null;
    const q = search.trim().toLowerCase();
    const list = q
      ? assets.filter((a) =>
          (a.name || '').toLowerCase().includes(q)
          || (a.siteName || '').toLowerCase().includes(q))
      : assets;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [assets, search]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Assets & QR stickers</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-ghost" onClick={() => setImportOpen(true)}>
            Bulk import (CSV)
          </button>
        </div>
      </div>

      <p className="settings-help" style={{ marginTop: 8 }}>
        Define each piece of equipment customers can report problems with. Generate a QR
        sticker per asset, print it, stick it on the unit. When someone scans, the ticket
        form opens pre-filled. Click an asset to see its service history.
      </p>

      {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}

      <form
        onSubmit={createAsset}
        style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          marginTop: 16, padding: 12,
          background: '#f8fafc', borderRadius: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>+ Add an asset</div>
        <input
          type="text"
          placeholder="Asset name (e.g. Walk-in freezer #3)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Site / location (optional)"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
          />
          <select
            value={defaultSkillCategoryId}
            onChange={(e) => setDefaultSkillCategoryId(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
          >
            <option value="">Default skill (optional)...</option>
            {skills.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={busy || !name.trim()} className="btn-primary" style={{ alignSelf: 'flex-start' }}>
          {busy ? 'Creating...' : '+ Add asset'}
        </button>
      </form>

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search by asset name or site..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          {filtered ? `${filtered.length} of ${assets.length}` : 'Loading...'}
        </span>
      </div>

      {filtered === null && <div className="placeholder" style={{ marginTop: 12 }}>Loading...</div>}
      {filtered && filtered.length === 0 && (
        <div className="placeholder" style={{ marginTop: 12 }}>
          {search ? 'No assets match your search.' : 'No assets yet. Add one above or bulk-import from CSV.'}
        </div>
      )}
      {filtered && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {filtered.map((a) => (
            <AssetRow
              key={a.id}
              asset={a}
              skills={skills}
              vendorId={vendorId}
              onShowQr={() => setQrFor(a)}
              onShowHistory={() => setHistoryFor(a)}
              onDelete={() => removeAsset(a.id)}
            />
          ))}
        </div>
      )}

      {qrFor && (
        <QrModal
          asset={qrFor}
          vendorId={vendorId}
          onClose={() => setQrFor(null)}
        />
      )}

      {historyFor && (
        <HistoryModal
          asset={historyFor}
          vendorId={vendorId}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {importOpen && (
        <BulkImportModal
          vendorId={vendorId}
          skills={skills}
          existingAssets={assets || []}
          onClose={() => setImportOpen(false)}
        />
      )}
    </>
  );
}

// ============================================================================
// AssetRow — one asset in the list, with ticket-count badge
// ============================================================================

function AssetRow({ asset, skills, vendorId, onShowQr, onShowHistory, onDelete }) {
  // Cheap ticket count — listens for tickets where assetId == this asset.
  // Limited to recent (last 90d) to keep the read cost bounded.
  const [ticketCount, setTicketCount] = useState(null);
  useEffect(() => {
    if (!vendorId || !asset?.id) return;
    const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('assetId', '==', asset.id),
    );
    const unsub = onSnapshot(q, (snap) => {
      // Filter client-side to "in the last 90 days" — avoids needing a composite index
      let count = 0;
      snap.forEach((doc) => {
        const t = doc.data();
        const created = t.createdAt?.toDate ? t.createdAt.toDate() : null;
        if (!created || created >= cutoff) count++;
      });
      setTicketCount(count);
    }, () => setTicketCount(null));
    return () => unsub();
  }, [vendorId, asset?.id]);

  const skillName = asset.defaultSkillCategoryId
    ? skills.find((s) => s.id === asset.defaultSkillCategoryId)?.name
    : null;

  return (
    <div className="asset-row" style={{ alignItems: 'center' }}>
      <div className="asset-row-meta" style={{ cursor: 'pointer' }} onClick={onShowHistory}>
        <div className="asset-row-name">
          {asset.name}
          {ticketCount !== null && ticketCount > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 600,
              padding: '2px 7px', borderRadius: 999,
              background: ticketCount >= 5 ? '#fee2e2' : '#fef3c7',
              color: ticketCount >= 5 ? '#991b1b' : '#92400e',
            }}>
              {ticketCount} ticket{ticketCount === 1 ? '' : 's'} / 90d
            </span>
          )}
        </div>
        <div className="asset-row-sub">
          {asset.siteName && <span>{asset.siteName}</span>}
          {skillName && <span> &middot; {skillName}</span>}
        </div>
      </div>
      <div className="asset-row-actions">
        <button onClick={onShowHistory} className="btn-ghost">History</button>
        <button onClick={onShowQr} className="btn-ghost">Show QR</button>
        <button
          onClick={onDelete}
          className="link-danger"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// HistoryModal — per-asset ticket history (Task #98)
// ============================================================================

function HistoryModal({ asset, vendorId, onClose }) {
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId || !asset?.id) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('assetId', '==', asset.id),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort client-side by createdAt desc — avoids needing a composite index.
      rows.sort((a, b) => {
        const ta = a.createdAt?.toDate?.() || new Date(0);
        const tb = b.createdAt?.toDate?.() || new Date(0);
        return tb - ta;
      });
      setTickets(rows);
    }, (err) => setError(err.message));
    return () => unsub();
  }, [vendorId, asset?.id]);

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 720 }}>
        <h3 style={{ marginBottom: 4 }}>{asset.name}</h3>
        <div className="settings-help" style={{ marginTop: 0 }}>
          {asset.siteName ? `${asset.siteName} · ` : ''}Ticket history
        </div>

        {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}

        {tickets === null && <div className="placeholder" style={{ marginTop: 12 }}>Loading...</div>}
        {tickets && tickets.length === 0 && (
          <div className="placeholder" style={{ marginTop: 12 }}>
            No tickets have ever been raised for this asset.
          </div>
        )}
        {tickets && tickets.length > 0 && (
          <div style={{ marginTop: 12, maxHeight: 400, overflowY: 'auto' }}>
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Requester</th>
                  <th>Skill</th>
                  <th>Status</th>
                  <th>Handled by</th>
                  <th>Rating</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td>{formatDateTime(t.createdAt)}</td>
                    <td>{t.customerName || '-'}</td>
                    <td>{t.skillName || '-'}</td>
                    <td>
                      <StatusPill status={t.status} />
                    </td>
                    <td>{t.assignedTo?.name || (t.status === 'ABANDONED' ? '(no answer)' : '-')}</td>
                    <td>
                      {t.feedback?.customer?.rating
                        ? <span style={{ color: '#f59e0b' }} title={t.feedback.customer.comment || ''}>
                            {'*'.repeat(t.feedback.customer.rating)}{'-'.repeat(5 - t.feedback.customer.rating)}
                          </span>
                        : <span style={{ color: 'var(--muted)' }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="ra-modal-actions" style={{ marginTop: 14 }}>
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  let bg = '#e5e7eb', fg = '#374151', label = status || '-';
  if (status === 'CLOSED') { bg = '#dcfce7'; fg = '#166534'; label = 'Resolved'; }
  else if (status === 'ASSIGNED') { bg = '#dbeafe'; fg = '#1e40af'; label = 'Assigned'; }
  else if (status?.startsWith('RINGING_')) { bg = '#fef3c7'; fg = '#92400e'; label = 'Ringing'; }
  else if (status === 'ABANDONED') { bg = '#fee2e2'; fg = '#991b1b'; label = 'Abandoned'; }
  else if (status === 'NEW' || status === 'ROUTING') { bg = '#fef3c7'; fg = '#92400e'; label = 'Routing'; }
  return (
    <span style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {label}
    </span>
  );
}

function formatDateTime(ts) {
  const d = ts?.toDate?.() || (ts instanceof Date ? ts : null);
  if (!d) return '-';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ============================================================================
// BulkImportModal — CSV upload (Task #96)
// ============================================================================

function BulkImportModal({ vendorId, skills, existingAssets, onClose }) {
  const [text, setText] = useState('');
  const [parseResult, setParseResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const t = String(reader.result || '');
      setText(t);
      processText(t);
    };
    reader.readAsText(file);
  }

  function processText(t) {
    try {
      const { headers, rows } = parseCSV(t);
      const required = ['name'];
      const missing = required.filter((h) => !headers.includes(h));
      if (missing.length) {
        setParseResult({ error: `CSV is missing required columns: ${missing.join(', ')}. Expected at least: name. Optional: siteName, defaultSkill, notes.` });
        return;
      }
      // Map each row to a candidate asset doc. Resolve skill by NAME (case-insensitive).
      const existingNames = new Set(existingAssets.map((a) => `${(a.name || '').toLowerCase()}|${(a.siteName || '').toLowerCase()}`));
      const candidates = rows.map((r, idx) => {
        const name = (r.name || '').trim();
        const siteName = (r.sitename || r.site || '').trim() || null;
        const skillNameQ = (r.defaultskill || r.skill || r.skillcategory || '').trim().toLowerCase();
        const skillMatch = skillNameQ ? skills.find((s) => (s.name || '').toLowerCase() === skillNameQ) : null;
        const notes = (r.notes || '').trim();
        const key = `${name.toLowerCase()}|${(siteName || '').toLowerCase()}`;
        const dup = existingNames.has(key);
        const skillUnresolved = skillNameQ && !skillMatch;
        const issues = [];
        if (!name) issues.push('missing name');
        if (dup) issues.push('already exists');
        if (skillUnresolved) issues.push(`unknown skill "${skillNameQ}"`);
        return {
          lineNum: idx + 2,
          name, siteName,
          defaultSkillCategoryId: skillMatch?.id || null,
          skillNameRaw: skillNameQ,
          notes,
          issues,
          willImport: name && !dup,
        };
      });
      setParseResult({ candidates });
    } catch (err) {
      setParseResult({ error: `Couldn't parse CSV: ${err.message}` });
    }
  }

  async function doImport() {
    if (!parseResult?.candidates) return;
    const toImport = parseResult.candidates.filter((c) => c.willImport);
    if (toImport.length === 0) return;
    setBusy(true);
    let added = 0, failed = 0;
    for (const c of toImport) {
      try {
        await addDoc(collection(db, 'vendors', vendorId, 'assets'), {
          name: c.name,
          siteName: c.siteName,
          defaultSkillCategoryId: c.defaultSkillCategoryId,
          notes: c.notes,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        added++;
      } catch {
        failed++;
      }
    }
    setBusy(false);
    setDone({ added, failed, total: toImport.length });
  }

  const willImportCount = parseResult?.candidates?.filter((c) => c.willImport).length || 0;
  const skipCount = parseResult?.candidates?.filter((c) => !c.willImport).length || 0;

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 720 }}>
        <h3>Bulk import assets from CSV</h3>

        {done ? (
          <>
            <p className="settings-help">
              Imported <strong>{done.added}</strong> asset{done.added === 1 ? '' : 's'}.
              {done.failed > 0 ? ` ${done.failed} failed.` : ''}
            </p>
            <div className="ra-modal-actions">
              <button className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p className="settings-help" style={{ marginTop: 0 }}>
              Upload a CSV with one asset per row. The first row should be the header.
              Required column: <code>name</code>. Optional columns: <code>siteName</code>,
              <code> defaultSkill</code> (skill category name as you've set it up — case-insensitive),
              <code> notes</code>.
              <br />
              Example header row: <code>name,siteName,defaultSkill,notes</code>
            </p>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                style={{ fontSize: 13 }}
              />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>or paste CSV below</span>
            </div>

            <textarea
              rows={6}
              value={text}
              onChange={(e) => { setText(e.target.value); processText(e.target.value); }}
              placeholder={'name,siteName,defaultSkill,notes\nWalk-in freezer #3,Westfield Cafe,Refrigeration,front of house\nGenerator A,Distribution Centre,Generators,'}
              className="ra-input"
              style={{ width: '100%', marginTop: 8, fontFamily: 'monospace', fontSize: 12 }}
            />

            {parseResult?.error && (
              <div className="error-banner" style={{ marginTop: 8 }}>{parseResult.error}</div>
            )}

            {parseResult?.candidates && parseResult.candidates.length > 0 && (
              <>
                <div style={{
                  marginTop: 12, padding: 10, background: '#f8fafc', borderRadius: 6,
                  fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap',
                }}>
                  <span><strong style={{ color: '#16a34a' }}>{willImportCount}</strong> will import</span>
                  <span><strong style={{ color: '#dc2626' }}>{skipCount}</strong> will skip (duplicates / missing name)</span>
                </div>

                <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                  <table className="data-table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Name</th>
                        <th>Site</th>
                        <th>Skill</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parseResult.candidates.map((c, i) => (
                        <tr key={i} style={{ opacity: c.willImport ? 1 : 0.55 }}>
                          <td>{c.lineNum}</td>
                          <td>{c.name || <em>(blank)</em>}</td>
                          <td>{c.siteName || '-'}</td>
                          <td>{c.defaultSkillCategoryId ? skills.find((s) => s.id === c.defaultSkillCategoryId)?.name : (c.skillNameRaw ? <span style={{ color: '#dc2626' }}>?{c.skillNameRaw}?</span> : '-')}</td>
                          <td>{c.willImport ? <span style={{ color: '#16a34a' }}>OK</span> : <span style={{ color: '#dc2626' }}>{c.issues.join(', ')}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="ra-modal-actions" style={{ marginTop: 14 }}>
              <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="btn-primary"
                onClick={doImport}
                disabled={busy || willImportCount === 0}
              >
                {busy ? 'Importing...' : `Import ${willImportCount} asset${willImportCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Simple CSV parser. Handles quoted fields with embedded commas + escaped quotes.
function parseCSV(text) {
  const lines = text.replace(/^﻿/, '').trim().split(/\r?\n/);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const cols = parseLine(raw);
    const row = {};
    headers.forEach((h, idx) => { row[h] = (cols[idx] || '').trim(); });
    rows.push(row);
  }
  return { headers, rows };
}

function parseLine(line) {
  const result = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (c === ',' && !inQuotes) {
      result.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  result.push(cur);
  return result;
}

// ============================================================================
// QrModal — unchanged from previous version
// ============================================================================

function QrModal({ asset, vendorId, onClose }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const url = `https://giraops.com/app?vendor=${vendorId}&asset=${asset.id}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const png = await QRCode.toDataURL(url, {
          errorCorrectionLevel: 'H',
          width: 600, margin: 2,
          color: { dark: '#0f172a', light: '#ffffff' },
        });
        if (!cancelled) setDataUrl(png);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  function downloadPng() {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `giraops-${slugify(asset.name)}.png`;
    a.click();
  }
  function copyUrl() { navigator.clipboard?.writeText(url).catch(() => {}); }
  function printSticker() {
    if (!dataUrl) return;
    const w = window.open('', '_blank', 'width=400,height=500');
    if (!w) return;
    w.document.write(`<!DOCTYPE html>
<html><head><title>Giraops sticker - ${escapeHtml(asset.name)}</title>
<style>
  body { font-family: sans-serif; margin: 16px; text-align: center; color: #0f172a; }
  .qr { display: block; margin: 0 auto 12px; width: 260px; height: 260px; }
  .name { font-size: 16px; font-weight: 600; margin: 4px 0; }
  .site { font-size: 13px; color: #64748b; }
  .footer { font-size: 11px; color: #94a3b8; margin-top: 12px; }
  @media print { body { margin: 4mm; } }
</style></head><body>
  <img class="qr" src="${dataUrl}" alt="QR" />
  <div class="name">${escapeHtml(asset.name)}</div>
  ${asset.siteName ? `<div class="site">${escapeHtml(asset.siteName)}</div>` : ''}
  <div class="footer">Scan to report an issue - Giraops</div>
  <script>window.onload = () => window.print();</script>
</body></html>`);
    w.document.close();
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 400, textAlign: 'center' }}>
        <h3>QR sticker</h3>
        <div className="settings-help" style={{ marginTop: -8, textAlign: 'left' }}>
          Print this and stick it on <strong>{asset.name}</strong>
          {asset.siteName ? ` at ${asset.siteName}` : ''}. Scanning opens a pre-filled ticket form.
        </div>
        {error && <div className="error-banner">{error}</div>}
        {dataUrl ? (
          <img src={dataUrl} alt="QR" style={{ width: 240, height: 240, margin: '16px auto', display: 'block' }} />
        ) : (
          <div className="placeholder" style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Generating...
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--muted)', wordBreak: 'break-all', margin: '8px 0 16px', textAlign: 'left' }}>
          {url}
        </div>
        <div className="ra-modal-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
          <button className="btn-ghost" onClick={copyUrl}>Copy URL</button>
          <button className="btn-ghost" onClick={downloadPng} disabled={!dataUrl}>Download PNG</button>
          <button className="btn-primary" onClick={printSticker} disabled={!dataUrl}>Print</button>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 14,
  background: 'var(--surface)',
  color: 'var(--text)',
};

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
